import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { api } from '../services/api';
import {
  Shield, Users, Hexagon, Clock, CheckCircle, XCircle,
  Loader2, MessageSquare, Send, Plus, Mail, Eye,
  UserPlus, Briefcase, ArrowRight, AlertCircle, AlertTriangle, Link2,
  BadgeCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { BlockchainAdminPanel } from '../components/BlockchainAdminPanel';

export default function AdminDashboard() {
  const { user } = useAuth();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('pipeline');
  const [stats, setStats] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [dtRequests, setDtRequests] = useState([]);
  const [messages, setMessages] = useState([]);
  const [supportInbox, setSupportInbox] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  // Modals
  const [showCreatePartner, setShowCreatePartner] = useState(false);
  const [showInviteBroker, setShowInviteBroker] = useState(false);
  const [showThread, setShowThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showDetail, setShowDetail] = useState(null);
  const [detailAsset, setDetailAsset] = useState(null);

  // Partner form
  const [partnerForm, setPartnerForm] = useState({ email: '', full_name: '', company: '' });
  const [brokerForm, setBrokerForm] = useState({ asset_id: '', broker_email: '', broker_name: '', policy_number: '' });

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/dashboard'); return; }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, pipelineRes, dtRes, threadsRes, supportRes, partnersRes] = await Promise.all([
        api.getAdminDashboard(),
        api.getTokenizationPipeline(),
        api.getDigitalTwinRequests(),
        api.getMessageThreads(),
        api.getSupportInbox(),
        api.getPartners(),
      ]);
      setStats(statsRes.data);
      setPipeline(pipelineRes.data);
      setDtRequests(dtRes.data || []);
      setMessages(threadsRes.data || []);
      setSupportInbox(supportRes.data || []);
      setPartners(partnersRes.data || []);
    } catch (err) { console.error('Admin fetch error:', err); }
    finally { setLoading(false); }
  };

  const handleApprove = async (requestId) => {
    setActionLoading(requestId);
    try { await api.approveVerification(requestId); toast.success('Approved!'); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);

  const openRejectModal = (request) => {
    setRejectFor(request);
    setRejectReason('');
  };

  const submitReject = async () => {
    if (!rejectFor) return;
    if (!rejectReason.trim()) { toast.error('Please provide a reason for rejection'); return; }
    setRejectLoading(true);
    try {
      await api.rejectVerification(rejectFor.id, { reason: rejectReason.trim(), send_message: true });
      toast.success('Rejected. The user has been notified with your reason.');
      setRejectFor(null);
      setShowDetail(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setRejectLoading(false);
    }
  };

  const handleApproveDT = async (userId) => {
    setActionLoading(`dt-${userId}`);
    try { await api.approveDigitalTwinAccess(userId); toast.success('DT access approved.'); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const handleRejectDT = async (userId) => {
    if (!window.confirm('Reject this Digital Twin Vault access request? The user will be notified.')) return;
    setActionLoading(`dt-${userId}`);
    try { await api.rejectDigitalTwinAccess(userId); toast.success('DT access rejected.'); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const handleCreatePartner = async () => {
    try {
      const res = await api.createPartner(partnerForm);
      toast.success(`Partner created! Temp password: ${res.data.temporary_password}`);
      setShowCreatePartner(false);
      setPartnerForm({ email: '', full_name: '', company: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create partner'); }
  };

  const openDetail = async (req) => {
    setShowDetail(req);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('assagart_token');
      const res = await fetch(`${API}/api/assets/${req.asset_id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setDetailAsset(await res.json());
      else setDetailAsset(null);
    } catch { setDetailAsset(null); }
  };

  const handleInviteBroker = async () => {
    try {
      const res = await api.inviteBroker(brokerForm);
      toast.success(`Broker invite sent! Link: ${res.data.verification_url}`);
      setShowInviteBroker(false);
      setBrokerForm({ asset_id: '', broker_email: '', broker_name: '', policy_number: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openThread = async (thread) => {
    setShowThread(thread);
    try {
      const res = await api.getThreadMessages(thread.id);
      setThreadMessages(res.data.messages || []);
    } catch { setThreadMessages([]); }
  };

  const sendReply = async () => {
    if (!newMessage.trim() || !showThread) return;
    try {
      await api.sendMessage({ thread_id: showThread.id, message: newMessage });
      setNewMessage('');
      const res = await api.getThreadMessages(showThread.id);
      setThreadMessages(res.data.messages || []);
    } catch { toast.error('Failed to send'); }
  };

  if (loading && !stats) {
    return <DashboardLayout><div className="flex items-center justify-center h-96"><Loader2 size={32} className="text-gold animate-spin" /></div></DashboardLayout>;
  }

  const tabs = [
    { key: 'pipeline', label: 'Tokenisation Pipeline', icon: Hexagon },
    { key: 'messages', label: 'Messages', icon: MessageSquare, badge: messages.length },
    { key: 'support', label: 'Support Inbox', icon: Mail, badge: supportInbox.filter(m => m.status === 'open').length },
    { key: 'partners', label: 'Partners', icon: Users },
    { key: 'access', label: 'DT Access', icon: Shield },
    { key: 'blockchain', label: 'Polygon Registry', icon: Link2 },
  ];

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-gold uppercase tracking-[0.2em] text-xs mb-2">Admin Control</p>
            <h1 className="font-serif text-3xl text-white">Command Centre</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/admin/brokers">
              <Button variant="outline" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs" data-testid="manage-brokers-btn">
                <BadgeCheck size={14} className="mr-1" /> Verified brokers
              </Button>
            </Link>
            <Button onClick={() => setShowCreatePartner(true)} variant="outline" className="border-gold/30 text-gold hover:bg-gold/10 text-xs" data-testid="create-partner-btn">
              <UserPlus size={14} className="mr-1" /> Create Partner
            </Button>
            <Button onClick={() => setShowInviteBroker(true)} variant="outline" className="border-[#627EEA]/30 text-[#627EEA] hover:bg-[#627EEA]/10 text-xs" data-testid="invite-broker-btn">
              <Send size={14} className="mr-1" /> Invite Broker
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="luxury-card p-4"><p className="text-xs text-muted-foreground">Total Users</p><p className="text-2xl text-white font-semibold">{stats.total_users}</p></div>
            <div className="luxury-card p-4"><p className="text-xs text-muted-foreground">Total Assets</p><p className="text-2xl text-white font-semibold">{stats.total_assets}</p></div>
            <div className="luxury-card p-4"><p className="text-xs text-muted-foreground">Pending Approvals</p><p className="text-2xl text-gold font-semibold">{pipeline ? pipeline.awaiting_admin?.length : 0}</p></div>
            <div className="luxury-card p-4"><p className="text-xs text-muted-foreground">Partners</p><p className="text-2xl text-white font-semibold">{partners.length}</p></div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-[#0A0A0A] border border-[#262626] p-1 rounded overflow-x-auto">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2 text-xs rounded whitespace-nowrap transition-colors ${activeTab === t.key ? 'bg-gold text-black' : 'text-muted-foreground hover:text-white'}`}>
              <t.icon size={14} /> {t.label}
              {t.badge > 0 && <span className="ml-1 text-[10px] bg-red-500 text-white px-1.5 rounded-full">{t.badge}</span>}
            </button>
          ))}
        </div>

        {/* Pipeline Tab */}
        {activeTab === 'pipeline' && pipeline && (
          <div className="space-y-6" data-testid="pipeline-view">
            {[
              { key: 'awaiting_broker', label: 'Awaiting Broker Verification', color: 'text-amber-400', items: pipeline.awaiting_broker },
              { key: 'awaiting_admin', label: 'Awaiting Admin Approval', color: 'text-[#627EEA]', items: pipeline.awaiting_admin },
              { key: 'approved', label: 'Approved', color: 'text-emerald-400', items: pipeline.approved },
              { key: 'rejected', label: 'Rejected', color: 'text-red-400', items: pipeline.rejected },
            ].map(section => (
              <div key={section.key}>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`w-2 h-2 rounded-full ${section.color.replace('text-', 'bg-')}`} />
                  <h3 className={`text-sm font-medium ${section.color}`}>{section.label} ({section.items?.length || 0})</h3>
                </div>
                {section.items?.length > 0 ? (
                  <div className="space-y-2">
                    {section.items.map((req, i) => (
                      <div key={i} className="luxury-card p-4 flex items-center justify-between cursor-pointer hover:border-gold/30 transition-colors" onClick={() => openDetail(req)}>
                        <div>
                          <p className="text-white text-sm">{req.asset_name || req.asset_id?.slice(0, 8)}</p>
                          <p className="text-xs text-muted-foreground">User: {req.user_email || req.user_id?.slice(0, 8)} {req.broker_name && `| Broker: ${req.broker_name}`}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Eye size={14} className="text-muted-foreground" />
                          {section.key === 'awaiting_admin' && (
                            <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                              <Button onClick={() => handleApprove(req.id)} disabled={!!actionLoading} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
                                <CheckCircle size={12} className="mr-1" /> Approve
                              </Button>
                              <Button onClick={() => openRejectModal(req)} disabled={!!actionLoading} size="sm" variant="outline" className="border-red-500/30 text-red-400 text-xs">
                                <XCircle size={12} className="mr-1" /> Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground pl-4">None</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="space-y-3" data-testid="messages-view">
            {messages.length === 0 ? (
              <div className="luxury-card p-8 text-center"><MessageSquare size={32} className="text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground text-sm">No messages yet.</p></div>
            ) : (
              messages.map(thread => (
                <div key={thread.id} className="luxury-card p-4 flex items-center justify-between cursor-pointer hover:border-gold/30 transition-colors" onClick={() => openThread(thread)}>
                  <div>
                    <p className="text-white text-sm font-medium">{thread.subject}</p>
                    <p className="text-xs text-muted-foreground truncate max-w-md">{thread.last_message}</p>
                  </div>
                  <div className="text-right"><p className="text-[10px] text-muted-foreground">{new Date(thread.updated_at).toLocaleDateString()}</p><Eye size={14} className="text-muted-foreground ml-auto" /></div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Support Inbox Tab */}
        {activeTab === 'support' && (
          <div className="space-y-3" data-testid="support-inbox">
            <p className="text-xs text-muted-foreground mb-4">Messages from in-app contact form. Forwarded automatically to <span className="text-gold">support@assagart.co.uk</span> so the team is notified in real time. Reply directly from your inbox to respond to the member.</p>
            {supportInbox.length === 0 ? (
              <div className="luxury-card p-8 text-center"><Mail size={32} className="text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground text-sm">No support messages.</p></div>
            ) : (
              supportInbox.map(msg => (
                <div key={msg.id} className="luxury-card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white text-sm font-medium">{msg.subject}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded ${msg.status === 'open' ? 'text-amber-400 bg-amber-400/10' : 'text-emerald-400 bg-emerald-400/10'}`}>{msg.status}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{msg.message}</p>
                  <p className="text-[10px] text-muted-foreground mt-2">From: {msg.sender_email} | {new Date(msg.created_at).toLocaleDateString()}</p>
                </div>
              ))
            )}
          </div>
        )}

        {/* Partners Tab */}
        {activeTab === 'partners' && (
          <div className="space-y-4" data-testid="partners-view">
            <div className="flex justify-between items-center">
              <p className="text-xs text-muted-foreground">{partners.length} partner account(s)</p>
              <Button onClick={() => setShowCreatePartner(true)} size="sm" className="bg-gold text-black text-xs"><Plus size={12} className="mr-1" /> New Partner</Button>
            </div>
            {partners.map(p => (
              <div key={p.id} className="luxury-card p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center"><Briefcase size={16} className="text-gold" /></div>
                <div className="flex-1"><p className="text-white text-sm font-medium">{p.full_name}</p><p className="text-xs text-muted-foreground">{p.email} {p.company && `| ${p.company}`}</p></div>
                <p className="text-[10px] text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        )}

        {/* DT Access Tab */}
        {activeTab === 'access' && (
          <div className="space-y-3" data-testid="dt-access-view">
            {dtRequests.length === 0 ? (
              <div className="luxury-card p-8 text-center"><Shield size={32} className="text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground text-sm">No DT access requests.</p></div>
            ) : (
              dtRequests.map(req => (
                <div key={req.id} className="luxury-card p-4 flex items-center justify-between">
                  <div><p className="text-white text-sm">{req.user_email}</p><p className="text-[10px] text-muted-foreground">Requested {new Date(req.created_at).toLocaleDateString()}</p></div>
                  {req.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button onClick={() => handleApproveDT(req.user_id)} disabled={!!actionLoading} size="sm" className="bg-emerald-600 text-white text-xs"><CheckCircle size={12} className="mr-1" /> Approve</Button>
                      <Button onClick={() => handleRejectDT(req.user_id)} disabled={!!actionLoading} size="sm" variant="outline" className="border-red-500/30 text-red-400 text-xs"><XCircle size={12} className="mr-1" /> Reject</Button>
                    </div>
                  )}
                  {req.status !== 'pending' && <span className={`text-xs ${req.status === 'approved' ? 'text-emerald-400' : 'text-red-400'}`}>{req.status}</span>}
                </div>
              ))
            )}
          </div>
        )}

        {/* Blockchain (Polygon Registry) Tab */}
        {activeTab === 'blockchain' && (
          <div data-testid="blockchain-view">
            <BlockchainAdminPanel />
          </div>
        )}

        {/* Create Partner Modal */}
        <Dialog open={showCreatePartner} onOpenChange={setShowCreatePartner}>
          <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-md" data-testid="create-partner-modal">
            <DialogHeader><DialogTitle className="font-serif text-white">Create Partner Account</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><label className="text-xs text-muted-foreground uppercase tracking-wider">Full Name</label><Input value={partnerForm.full_name} onChange={e => setPartnerForm({...partnerForm, full_name: e.target.value})} placeholder="Victoria Sterling" className="luxury-input mt-1" /></div>
              <div><label className="text-xs text-muted-foreground uppercase tracking-wider">Email</label><Input value={partnerForm.email} onChange={e => setPartnerForm({...partnerForm, email: e.target.value})} placeholder="partner@wealthco.com" className="luxury-input mt-1" /></div>
              <div><label className="text-xs text-muted-foreground uppercase tracking-wider">Company</label><Input value={partnerForm.company} onChange={e => setPartnerForm({...partnerForm, company: e.target.value})} placeholder="Sterling Wealth Management" className="luxury-input mt-1" /></div>
              <Button onClick={handleCreatePartner} className="w-full bg-gold text-black hover:bg-gold/90">Create Partner Account</Button>
              <p className="text-[10px] text-muted-foreground text-center">A temporary password will be generated. Share it securely with the wealth manager.</p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Invite Broker Modal */}
        <Dialog open={showInviteBroker} onOpenChange={setShowInviteBroker}>
          <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-md" data-testid="invite-broker-modal">
            <DialogHeader><DialogTitle className="font-serif text-white">Invite Insurance Broker</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><label className="text-xs text-muted-foreground uppercase tracking-wider">Asset ID</label><Input value={brokerForm.asset_id} onChange={e => setBrokerForm({...brokerForm, asset_id: e.target.value})} placeholder="Asset ID to verify" className="luxury-input mt-1" /></div>
              <div><label className="text-xs text-muted-foreground uppercase tracking-wider">Broker Name</label><Input value={brokerForm.broker_name} onChange={e => setBrokerForm({...brokerForm, broker_name: e.target.value})} placeholder="James Wilson" className="luxury-input mt-1" /></div>
              <div><label className="text-xs text-muted-foreground uppercase tracking-wider">Broker Email</label><Input value={brokerForm.broker_email} onChange={e => setBrokerForm({...brokerForm, broker_email: e.target.value})} placeholder="broker@lloyds.com" className="luxury-input mt-1" /></div>
              <div><label className="text-xs text-muted-foreground uppercase tracking-wider">Policy Number</label><Input value={brokerForm.policy_number} onChange={e => setBrokerForm({...brokerForm, policy_number: e.target.value})} placeholder="POL-2024-AXA-003892" className="luxury-input mt-1" /></div>
              <Button onClick={handleInviteBroker} className="w-full bg-[#627EEA] hover:bg-[#5570D0] text-white">Send Verification Link</Button>
              <p className="text-[10px] text-muted-foreground text-center">A one-time verification link will be generated. Share it with the broker via email.</p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Thread View Modal */}
        <Dialog open={!!showThread} onOpenChange={() => setShowThread(null)}>
          <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader><DialogTitle className="font-serif text-white">{showThread?.subject}</DialogTitle></DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-3 py-4">
              {threadMessages.map(msg => (
                <div key={msg.id} className={`p-3 rounded-lg max-w-[80%] ${msg.sender_role === 'admin' ? 'bg-gold/10 border border-gold/20 ml-auto' : 'bg-[#0F0F0F] border border-[#1A1A1A]'}`}>
                  <p className="text-xs text-white">{msg.message}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{msg.sender_email} | {new Date(msg.created_at).toLocaleTimeString()}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2 pt-2 border-t border-[#1A1A1A]">
              <Input value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Type a reply..." className="luxury-input flex-1" onKeyDown={e => e.key === 'Enter' && sendReply()} />
              <Button onClick={sendReply} className="bg-gold text-black"><Send size={14} /></Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Asset Detail Modal */}
        <Dialog open={!!showDetail} onOpenChange={() => { setShowDetail(null); setDetailAsset(null); }}>
          <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pipeline-detail-modal">
            <DialogHeader><DialogTitle className="font-serif text-white">Tokenisation Request Details</DialogTitle></DialogHeader>
            {detailAsset ? (
              <div className="space-y-5">
                {/* Image */}
                {detailAsset.image_url && (
                  <div className="h-48 rounded-lg overflow-hidden bg-[#0F0F0F]">
                    <img src={detailAsset.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${detailAsset.image_url}` : detailAsset.image_url} alt={detailAsset.name} className="w-full h-full object-cover" />
                  </div>
                )}

                {/* Asset Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider">Asset Name</p><p className="text-white text-sm">{detailAsset.name}</p></div>
                  <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider">Category</p><p className="text-white text-sm capitalize">{detailAsset.category}</p></div>
                  <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider">Purchase Price</p><p className="text-white text-sm">{formatCurrency(detailAsset.purchase_price)}</p></div>
                  <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider">Current Value</p><p className="text-gold text-sm">{formatCurrency(detailAsset.current_value)}</p></div>
                  <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider">Location/Storage</p><p className="text-white text-sm">{detailAsset.location || 'Not specified'}</p></div>
                  <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider">Condition</p><p className="text-white text-sm">{detailAsset.condition || 'Not specified'}</p></div>
                </div>

                {/* Insurance */}
                <div className="p-4 bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg">
                  <p className="text-[10px] text-gold uppercase tracking-wider mb-2">Insurance Details</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><p className="text-[10px] text-muted-foreground">Provider</p><p className="text-white text-xs">{detailAsset.insurance_provider || 'Not provided'}</p></div>
                    <div><p className="text-[10px] text-muted-foreground">Policy Number</p><p className="text-white text-xs font-mono">{detailAsset.insurance_policy || 'Not provided'}</p></div>
                  </div>
                  {showDetail?.broker_name && (
                    <div className="mt-2 pt-2 border-t border-[#1A1A1A]">
                      <p className="text-[10px] text-muted-foreground">Broker Verification: <span className={showDetail.broker_status === 'verified' ? 'text-emerald-400' : 'text-amber-400'}>{showDetail.broker_status}</span> by {showDetail.broker_name}</p>
                    </div>
                  )}
                </div>

                {/* Description */}
                {detailAsset.description && (
                  <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Description</p><p className="text-xs text-muted-foreground leading-relaxed">{detailAsset.description}</p></div>
                )}

                {/* Notes */}
                {detailAsset.notes && (
                  <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">User Notes</p><p className="text-xs text-muted-foreground leading-relaxed">{detailAsset.notes}</p></div>
                )}

                {/* Actions */}
                {showDetail?.status === 'pending' && (
                  <div className="flex gap-3 pt-2">
                    <Button onClick={() => { handleApprove(showDetail.id); setShowDetail(null); }} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white">
                      <CheckCircle size={14} className="mr-2" /> Approve Tokenisation
                    </Button>
                    <Button onClick={() => openRejectModal(showDetail)} variant="outline" className="flex-1 border-red-500/30 text-red-400" data-testid="detail-reject-btn">
                      <XCircle size={14} className="mr-2" /> Reject
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-8 text-center"><Loader2 size={24} className="text-gold animate-spin mx-auto" /><p className="text-xs text-muted-foreground mt-2">Loading asset details...</p></div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reject Verification Modal - capture reason + auto-message user */}
        <Dialog open={!!rejectFor} onOpenChange={(o) => { if (!o) { setRejectFor(null); setRejectReason(''); } }}>
          <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-md" data-testid="reject-modal">
            <DialogHeader>
              <DialogTitle className="font-serif text-white">Reject Tokenization Request</DialogTitle>
            </DialogHeader>
            {rejectFor && (
              <div className="space-y-4">
                <div className="p-3 bg-[#0F0F0F] border border-[#1A1A1A] rounded">
                  <p className="text-white text-sm font-medium">{rejectFor.asset_name || 'Asset'}</p>
                  <p className="text-[11px] text-muted-foreground">{rejectFor.user_email || rejectFor.requested_by || 'User'}</p>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground">Reason for rejection</label>
                  <Textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={5}
                    className="luxury-input mt-1 resize-none"
                    placeholder="e.g. Insurance certificate is expired. Please attach a current policy showing this asset is covered for at least its declared value, then resubmit."
                    data-testid="reject-reason-input"
                  />
                </div>
                <div className="flex items-start gap-2 p-3 bg-amber-500/5 border border-amber-500/20 rounded">
                  <AlertTriangle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-amber-200/80 leading-relaxed">
                    The user will receive a notification, an in-app message they can reply to, and (if SendGrid is configured) an email - all containing your reason.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1 border-white/10 text-muted-foreground" onClick={() => { setRejectFor(null); setRejectReason(''); }}>Cancel</Button>
                  <Button
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    disabled={rejectLoading || !rejectReason.trim()}
                    onClick={submitReject}
                    data-testid="submit-reject-btn"
                  >
                    {rejectLoading ? <Loader2 size={14} className="animate-spin mr-2" /> : <XCircle size={14} className="mr-2" />}
                    Reject &amp; Notify
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
